import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { AronPendingWrite } from "@/features/aron/types";
import type {
  AronMailAlias,
  AronMailAliasTarget,
  InboundEmail,
  InboundEmailDetail,
} from "../types";

export const aronMailKeys = {
  emails: ["aron-mail", "emails"] as const,
  email: (id: string) => ["aron-mail", "emails", id] as const,
  aliases: ["aron-mail", "aliases"] as const,
  aliasTargets: ["aron-mail", "alias-targets"] as const,
};

export function useInboundEmails(filters?: { status?: string; search?: string }) {
  return useQuery({
    queryKey: [...aronMailKeys.emails, filters ?? {}],
    queryFn: async () => {
      const r = await apiClient.get<{ data: InboundEmail[] }>("/aron/mail/emails/", {
        params: filters,
      });
      return r.data.data;
    },
    // The mailbox fills up in the background (Celery poll) — keep the list fresh.
    refetchInterval: 30_000,
  });
}

export function useInboundEmail(id: string | null) {
  return useQuery({
    queryKey: id ? aronMailKeys.email(id) : ["aron-mail", "noop"],
    enabled: !!id,
    queryFn: async () => {
      const r = await apiClient.get<{ data: InboundEmailDetail }>(
        `/aron/mail/emails/${id}/`,
      );
      return r.data.data;
    },
    // Processing happens in the background and the owner write gate
    // (AronWriteProposal) invalidates ARON keys, not ours — poll to stay fresh.
    // While ARON is actively working the email, poll fast so the live progress
    // (tool steps) advances smoothly; back off to 10 s once it settles.
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "processing" || s === "received" ? 3_000 : 10_000;
    },
  });
}

/** Force an immediate Celery mailbox poll (the panel refresh button) instead of
 * waiting for the 5-minute beat. Ingest is async — refetch the list shortly after. */
export function usePollMailbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.post("/aron/mail/emails/poll/", {});
    },
    onSuccess: () => {
      // Give the worker a moment to ingest, then refresh the panel.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: aronMailKeys.emails });
      }, 3000);
    },
  });
}

export function useReprocessEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: InboundEmail }>(
        `/aron/mail/emails/${id}/reprocess/`,
      );
      return r.data.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: aronMailKeys.emails });
      qc.invalidateQueries({ queryKey: aronMailKeys.email(id) });
    },
  });
}

/** Cancel an in-flight processing run (the email is in `processing`). Cooperative
 * on the backend — the task stops at its next checkpoint and marks the email
 * `cancelled` (no reply is sent). */
export function useCancelEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await apiClient.post<{ data: InboundEmail }>(
        `/aron/mail/emails/${id}/cancel/`,
      );
      return r.data.data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: aronMailKeys.emails });
      qc.invalidateQueries({ queryKey: aronMailKeys.email(id) });
    },
  });
}

// ── CEO on-behalf write gate (email-sourced conversations of OTHER users) ─────
// The owner path stays on the standard /aron/conversations/…/writes/ endpoints
// (AronWriteProposal); these two act as the approving CEO.

export function useCeoExecuteEmailWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      writeId: string;
      emailId: string;
      body?: Record<string, unknown>;
      followUps?: Record<string, unknown>[];
      chainAction?: string;
    }) => {
      const r = await apiClient.post<{ data: AronPendingWrite }>(
        `/aron/mail/admin/writes/${vars.writeId}/execute/`,
        {
          ...(vars.body ? { body: vars.body } : {}),
          ...(vars.followUps ? { follow_ups: vars.followUps } : {}),
          ...(vars.chainAction ? { chain_action: vars.chainAction } : {}),
        },
      );
      return r.data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: aronMailKeys.email(vars.emailId) });
      qc.invalidateQueries({ queryKey: aronMailKeys.emails });
    },
  });
}

/** Re-run only the follow-up steps that failed on an executed email write. */
export function useCeoRetryEmailWriteSteps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { writeId: string; emailId: string }) => {
      const r = await apiClient.post<{ data: AronPendingWrite }>(
        `/aron/mail/admin/writes/${vars.writeId}/retry-steps/`,
        {},
      );
      return r.data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: aronMailKeys.email(vars.emailId) });
      qc.invalidateQueries({ queryKey: aronMailKeys.emails });
    },
  });
}

export function useCeoRejectEmailWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { writeId: string; emailId: string }) => {
      const r = await apiClient.post<{ data: AronPendingWrite }>(
        `/aron/mail/admin/writes/${vars.writeId}/reject/`,
      );
      return r.data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: aronMailKeys.email(vars.emailId) });
      qc.invalidateQueries({ queryKey: aronMailKeys.emails });
    },
  });
}

// ── Aliases (CEO-managed private/extra sender addresses) ──────────────────────

export function useAronMailAliases() {
  return useQuery({
    queryKey: aronMailKeys.aliases,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronMailAlias[] }>("/aron/mail/aliases/");
      return r.data.data;
    },
  });
}

/** Valid identities an alias may map to — active internal users with ARON access. */
export function useAronMailAliasTargets() {
  return useQuery({
    queryKey: aronMailKeys.aliasTargets,
    queryFn: async () => {
      const r = await apiClient.get<{ data: AronMailAliasTarget[] }>(
        "/aron/mail/aliases/targets/",
      );
      return r.data.data;
    },
  });
}

export function useCreateAronMailAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { user: string; email: string }) => {
      const r = await apiClient.post<{ data: AronMailAlias }>(
        "/aron/mail/aliases/",
        vars,
      );
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: aronMailKeys.aliases }),
  });
}

/** Toggle an alias active/inactive (an inactive alias stops being accepted). */
export function useSetAronMailAliasActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; is_active: boolean }) => {
      const r = await apiClient.patch<{ data: AronMailAlias }>(
        `/aron/mail/aliases/${vars.id}/`,
        { is_active: vars.is_active },
      );
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: aronMailKeys.aliases }),
  });
}

export function useDeleteAronMailAlias() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/aron/mail/aliases/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: aronMailKeys.aliases }),
  });
}
